import React from 'react';

interface ProgressStep {
  id: string;
  label: string;
  completed: boolean;
  current?: boolean;
}

interface EnhancedProgressBarProps {
  current: number;
  total: number;
  variant?: 'linear' | 'circular' | 'steps';
  showPercentage?: boolean;
  showSteps?: boolean;
  steps?: ProgressStep[];
  size?: 'sm' | 'md' | 'lg';
  color?: 'blue' | 'green' | 'purple' | 'orange';
  animated?: boolean;
  className?: string;
}

const EnhancedProgressBar: React.FC<EnhancedProgressBarProps> = ({
  current,
  total,
  variant = 'linear',
  showPercentage = true,
  showSteps = false,
  steps,
  size = 'md',
  color = 'blue',
  animated = true,
  className = ''
}) => {
  const progress = Math.min((current / total) * 100, 100);

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return { bar: 'h-1', text: 'text-xs', circle: 'w-16 h-16' };
      case 'lg':
        return { bar: 'h-4', text: 'text-base', circle: 'w-24 h-24' };
      default:
        return { bar: 'h-2', text: 'text-sm', circle: 'w-20 h-20' };
    }
  };

  const getColorClasses = () => {
    switch (color) {
      case 'green':
        return { 
          bg: 'bg-green-500', 
          text: 'text-green-600',
          ring: 'stroke-green-500',
          step: 'bg-green-500 border-green-500'
        };
      case 'purple':
        return { 
          bg: 'bg-purple-500', 
          text: 'text-purple-600',
          ring: 'stroke-purple-500',
          step: 'bg-purple-500 border-purple-500'
        };
      case 'orange':
        return { 
          bg: 'bg-orange-500', 
          text: 'text-orange-600',
          ring: 'stroke-orange-500',
          step: 'bg-orange-500 border-orange-500'
        };
      default:
        return { 
          bg: 'bg-blue-500', 
          text: 'text-blue-600',
          ring: 'stroke-blue-500',
          step: 'bg-blue-500 border-blue-500'
        };
    }
  };

  const sizeClasses = getSizeClasses();
  const colorClasses = getColorClasses();

  const renderLinearProgress = () => (
    <div className={`space-y-2 ${className}`}>
      <div className="flex justify-between items-center">
        <span className={`${sizeClasses.text} font-medium text-gray-700`}>
          {showSteps ? `Étape ${current}/${total}` : 'Progression'}
        </span>
        {showPercentage && (
          <span className={`${sizeClasses.text} font-medium ${colorClasses.text}`}>
            {Math.round(progress)}%
          </span>
        )}
      </div>
      <div className={`${sizeClasses.bar} bg-gray-200 rounded-full overflow-hidden`}>
        <div 
          className={`${sizeClasses.bar} ${colorClasses.bg} rounded-full ${animated ? 'transition-all duration-500 ease-out' : ''}`}
          style={{ width: `${progress}%` }}
        >
          {animated && (
            <div className="h-full bg-white bg-opacity-30 animate-pulse" />
          )}
        </div>
      </div>
    </div>
  );

  const renderCircularProgress = () => {
    const radius = 30;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <div className={`flex flex-col items-center space-y-2 ${className}`}>
        <div className="relative">
          <svg className={`${sizeClasses.circle} transform -rotate-90`}>
            <circle
              cx="50%"
              cy="50%"
              r={radius}
              stroke="currentColor"
              strokeWidth="4"
              fill="transparent"
              className="text-gray-200"
            />
            <circle
              cx="50%"
              cy="50%"
              r={radius}
              stroke="currentColor"
              strokeWidth="4"
              fill="transparent"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className={`${colorClasses.ring} ${animated ? 'transition-all duration-500 ease-out' : ''}`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`${sizeClasses.text} font-bold ${colorClasses.text}`}>
              {Math.round(progress)}%
            </span>
          </div>
        </div>
        <span className={`${sizeClasses.text} font-medium text-gray-700`}>
          Étape {current}/{total}
        </span>
      </div>
    );
  };

  const renderStepsProgress = () => {
    const stepsToRender = steps || Array.from({ length: total }, (_, index) => ({
      id: `step-${index + 1}`,
      label: `Étape ${index + 1}`,
      completed: index < current,
      current: index === current - 1
    }));

    return (
      <div className={`space-y-4 ${className}`}>
        {showPercentage && (
          <div className="flex justify-between items-center">
            <span className={`${sizeClasses.text} font-medium text-gray-700`}>
              Progression
            </span>
            <span className={`${sizeClasses.text} font-medium ${colorClasses.text}`}>
              {Math.round(progress)}%
            </span>
          </div>
        )}
        
        <div className="flex items-center justify-between mx-4">
          {stepsToRender.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center mx-2">
                <div
                  className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${animated ? 'transition-all duration-300' : ''} ${
                    step.completed
                      ? `${colorClasses.step} text-white`
                      : step.current
                      ? `border-${color}-500 bg-${color}-50 text-${color}-600`
                      : 'border-gray-300 bg-white text-gray-400'
                  }`}
                >
                  {step.completed ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span className="text-xs font-medium">{index + 1}</span>
                  )}
                </div>
                <span className={`mt-3 text-xs text-center ${step.current ? colorClasses.text : 'text-gray-500'}`}>
                  {step.label}
                </span>
              </div>
              
              {index < stepsToRender.length - 1 && (
                <div className={`flex-1 h-0.5 mx-3 ${step.completed ? colorClasses.bg : 'bg-gray-200'} ${animated ? 'transition-all duration-300' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  switch (variant) {
    case 'circular':
      return renderCircularProgress();
    case 'steps':
      return renderStepsProgress();
    default:
      return renderLinearProgress();
  }
};

export default EnhancedProgressBar;
